import { Link } from 'react-router-dom'
import { BrandLogo } from '../../shared/BrandLogo'
import '../web-test-motion.css'
import '../web-test-hero.css'
import '../web-test-legal.css'
import { WebsiteThemeToggle } from '../WebsiteThemeToggle'
import { portalLinks } from '../portal-links'
import { HERO_RISK_NOTICE } from '../risk-copy'
import { SeoHead } from '../seo/SeoHead'
import { defaultPageGraph } from '../seo/jsonld'
import { getPageSeo } from '../seo/pages'
import { trackClientLogin, trackOpenAccount } from '../../analytics/track'

export type LandingSection = {
  heading: string
  paragraphs: string[]
  bullets?: string[]
}

export type LandingDoc = {
  path: string
  eyebrow: string
  sections: LandingSection[]
  related: { label: string; to: string }[]
}

type Props = {
  doc: LandingDoc
}

export function SeoLandingPage({ doc }: Props) {
  const seo = getPageSeo(doc.path)
  if (!seo) return null

  return (
    <div className="wt-page wl-page">
      <SeoHead path={doc.path} jsonLd={defaultPageGraph(seo)} />
      <header className="wl-header">
        <div className="wl-header__inner">
          <BrandLogo className="wt-logo" tone="light" to="/" />
          <nav className="wl-header__nav" aria-label="Secondary">
            <Link to="/blog">Blog</Link>
            <Link to="/how-perfect-predict-works">How it works</Link>
            <Link to="/prediction-strategies">Strategies</Link>
            <Link to="/about">About</Link>
            <Link to="/support">Support</Link>
          </nav>
          <div className="wl-header__actions">
            <WebsiteThemeToggle />
            <a
              className="wt-btn wt-btn--ghost"
              href={portalLinks.login}
              onClick={() => trackClientLogin('landing_page')}
            >
              Sign In
            </a>
            <a
              className="wt-btn wt-btn--primary"
              href={portalLinks.register}
              onClick={() => trackOpenAccount('landing_page')}
            >
              Open Live Account
            </a>
          </div>
        </div>
      </header>

      <main className="wl-main">
        <div className="wl-inner">
          <p className="wl-eyebrow">{doc.eyebrow}</p>
          <h1 className="wl-title">{seo.h1}</h1>
          <p className="wl-section" style={{ color: 'var(--wt-muted)' }}>
            {seo.summary}
          </p>
          <p className="wl-section" style={{ color: 'var(--wt-muted)', fontSize: '0.95rem' }}>
            {HERO_RISK_NOTICE}{' '}
            <Link to="/risk-disclosure">Read the Risk Disclosure</Link>.
          </p>

          {doc.sections.map((section) => (
            <section key={section.heading} className="wl-section">
              <h2 style={{ fontSize: '1.15rem', margin: '0 0 8px', color: 'var(--wt-navy)' }}>
                {section.heading}
              </h2>
              {section.paragraphs.map((p) => (
                <p key={p.slice(0, 48)} style={{ margin: '0 0 10px', color: 'var(--wt-muted)' }}>
                  {p}
                </p>
              ))}
              {section.bullets ? (
                <ul style={{ margin: '0 0 8px', paddingLeft: '1.2rem', color: 'var(--wt-muted)' }}>
                  {section.bullets.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}

          <section className="wl-section">
            <h2 style={{ fontSize: '1.15rem', margin: '0 0 8px', color: 'var(--wt-navy)' }}>
              Next steps
            </h2>
            <p style={{ margin: '0 0 14px', color: 'var(--wt-muted)' }}>
              Create a Live account in the Client Portal, or explore related guides on this site.
            </p>
            <p className="wl-back" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              <a
                className="wt-btn wt-btn--primary"
                href={portalLinks.register}
                onClick={() => trackOpenAccount('landing_page')}
              >
                Open Live Account
              </a>
              <a
                className="wt-btn wt-btn--ghost"
                href={portalLinks.login}
                onClick={() => trackClientLogin('landing_page')}
              >
                Client Portal Login
              </a>
            </p>
          </section>

          <nav className="wl-section" aria-label="Related pages">
            <h2 style={{ fontSize: '1.15rem', margin: '0 0 8px', color: 'var(--wt-navy)' }}>
              Related
            </h2>
            <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
              {doc.related.map((item) => (
                <li key={item.to}>
                  <Link to={item.to}>{item.label}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <p className="wl-back">
            <Link to="/">← Back to Perfect Predict</Link>
          </p>
        </div>
      </main>
    </div>
  )
}
